import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  twitterHandle?: string;
  schema?: any;
  noIndex?: boolean;
}

const TITLE_SUFFIX = ' | Eclipse Group';
// Google truncates titles at roughly 60 characters; past that the brand
// suffix is what gets cut, so drop it instead of the page's own words.
const MAX_TITLE_LENGTH = 60;
const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';
// Snippets are cut at roughly 155-160 characters.
const MAX_DESCRIPTION_LENGTH = 158;

// Below this, whole sentences leave too much of the snippet unused.
const MIN_SENTENCE_FIT_LENGTH = 110;

// Keep whole sentences while they fit. If that leaves the snippet short,
// cut the text at a word boundary instead.
function clampDescription(text: string): string {
  if (text.length <= MAX_DESCRIPTION_LENGTH) return text;

  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) ?? [];
  const fitted = sentences.reduce(
    (kept, sentence) => {
      const next = kept.text + sentence;
      return kept.done || next.trim().length > MAX_DESCRIPTION_LENGTH
        ? { ...kept, done: true }
        : { text: next, done: false };
    },
    { text: '', done: false },
  ).text.trim();
  if (fitted.length >= MIN_SENTENCE_FIT_LENGTH) return fitted;

  const cut = text.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:\s]+$/, '')}…`;
}

const SEO: React.FC<SEOProps> = ({
  title = "Voyara AI — Intelligent Travel Systems",
  description = "Voyara AI by Eclipse Group: an AI travel intelligence platform for planning, booking and coordinating trips. A product of Eclipse Private Limited.",
  canonical = "https://www.eclipsegroup.in/",
  ogType = "website",
  ogImage = "https://www.eclipsegroup.in/og-image.png",
  twitterHandle = "@eclipsegroupin",
  schema,
  noIndex = false,
}) => {
  const withSuffix = `${title}${TITLE_SUFFIX}`;
  const fullTitle = withSuffix.length <= MAX_TITLE_LENGTH ? withSuffix : title;
  const metaDescription = clampDescription(description);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      {/* follow: noindexed pages still pass their links on to indexed ones. */}
      {noIndex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={OG_IMAGE_WIDTH} />
      <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Voyara AI" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}

      {/* AI Search Optimization */}
      <meta name="llms-txt" content="https://www.eclipsegroup.in/llms.txt" />
    </Helmet>
  );
};

export default SEO;
